/**
 * Shared Grátis vs Equipe plan picker — shadcn Card/Button throughout.
 * Used by public /pricing and the free-user Conta → Assinatura tab.
 */
import {
  AMOUNTS_CENTAVOS,
  BASE_TEAM_SEATS,
  EXTRA_SEAT_PRICE_CENTAVOS,
  SEAT_PACK_INCLUDED_INBOUND,
  INBOUND_FREE_MONTHLY,
  FREE_INCLUDED_SEATS,
} from '@x9/billing';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';

type Props = {
  onSelectMonthly: () => void;
  onSelectYearly: () => void;
  /** Hide the free-tier card (Conta already shows "Plano gratuito"). */
  showFreeTier?: boolean;
};

function brl(cents: number): string {
  return (cents / 100).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });
}

function Feature({ children }: { children: React.ReactNode }) {
  return (
    <li className="flex items-start gap-2 text-sm text-foreground">
      <span aria-hidden className="mt-0.5 text-foreground">✓</span>
      <span>{children}</span>
    </li>
  );
}

export default function PlanPicker({
  onSelectMonthly,
  onSelectYearly,
  showFreeTier = true,
}: Props) {
  return (
    <div className="space-y-3">
      {showFreeTier && (
        <Card>
          <CardHeader>
            <div className="flex items-baseline justify-between gap-2">
              <CardTitle>Grátis</CardTitle>
              <p className="text-2xl font-bold text-foreground">R$ 0</p>
            </div>
            <CardDescription>
              {FREE_INCLUDED_SEATS} assento nominal de colega incluído. Mais assentos ou
              volume de contribuições no plano Equipe.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ul className="space-y-1.5">
              <Feature>Rotinas, tarefas e pastas ilimitadas para você mesmo</Feature>
              <Feature>
                {FREE_INCLUDED_SEATS} assento nominal de colega — atribua uma rotina a
                uma outra pessoa
              </Feature>
              <Feature>Suas próprias capturas nunca contam em nada</Feature>
              <Feature>
                {INBOUND_FREE_MONTHLY} contribuições de outras pessoas por mês (QR / web)
              </Feature>
            </ul>
          </CardContent>
        </Card>
      )}

      <Card className="border-2 border-border">
        <CardHeader>
          <div className="flex items-center justify-between gap-2">
            <CardTitle>{showFreeTier ? 'Equipe' : 'Plano Equipe'}</CardTitle>
            <Badge>{BASE_TEAM_SEATS} assentos</Badge>
          </div>
          {!showFreeTier && (
            <CardDescription>
              {BASE_TEAM_SEATS} assentos nominais para atribuir rotinas e acompanhar a execução,
              +{SEAT_PACK_INCLUDED_INBOUND} contribuições/mês e retenção estendida. Assentos
              extras a {(EXTRA_SEAT_PRICE_CENTAVOS / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}/mês.
            </CardDescription>
          )}
          {showFreeTier && (
            <p className="text-3xl font-bold text-foreground">
              {brl(AMOUNTS_CENTAVOS.MONTHLY)}
              <span className="text-base font-medium text-muted-foreground">/mês</span>
            </p>
          )}
        </CardHeader>
        <CardContent className="space-y-3">
          {showFreeTier && (
            <ul className="space-y-1.5">
              <Feature>Tudo do plano grátis</Feature>
              <Feature>
                {BASE_TEAM_SEATS} assentos nominais — atribua rotinas e acompanhe a
                execução da sua equipe (extras a{' '}
                {(EXTRA_SEAT_PRICE_CENTAVOS / 100).toLocaleString('pt-BR', {
                  style: 'currency',
                  currency: 'BRL',
                })}
                /mês)
              </Feature>
              <Feature>+{SEAT_PACK_INCLUDED_INBOUND} contribuições de outras pessoas por mês</Feature>
              <Feature>Retenção estendida do histórico</Feature>
            </ul>
          )}
          <div className="flex flex-col gap-2 lg:flex-row">
            <Button className="lg:flex-1" onClick={onSelectYearly}>
              Anual · {brl(AMOUNTS_CENTAVOS.ANNUAL)} (−20%)
            </Button>
            <Button variant="secondary" className="lg:flex-1" onClick={onSelectMonthly}>
              Mensal · {brl(AMOUNTS_CENTAVOS.MONTHLY)}/mês
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
